import './header.scss'

const AGE_COOKIE_NAME = 'sb_age_verified'

/** @type {() => void} */
let closeNavbarDropdowns = () => {}

const DROPDOWN_INLINE_PROPS = [
	'position',
	'left',
	'right',
	'top',
	'width',
	'min-width',
	'max-width',
	'margin-left',
	'box-sizing',
]

/**
 * Replaces UIkit’s data-uk-dropdown="{ justify: 'body', mode: 'click' }" for the primary nav.
 * Theme CSS only shows .uk-dropdown when the parent li has .uk-open.
 */
function clearDropdownPosition( dropdown ) {
	DROPDOWN_INLINE_PROPS.forEach( ( p ) => dropdown.style.removeProperty( p ) )
}

function applyBodyJustify( li ) {
	const dropdown = li.querySelector( ':scope > .uk-dropdown' )
	if ( ! dropdown ) return

	const anchor = document.querySelector( '.js-headerSticky' ) || document.querySelector( '.wxpnavbar' )
	const topPx = anchor ? anchor.getBoundingClientRect().bottom : li.getBoundingClientRect().bottom

	Object.assign( dropdown.style, {
		position: 'fixed',
		left: '0',
		right: '0',
		width: '100vw',
		minWidth: '100vw',
		maxWidth: '100vw',
		marginLeft: '0',
		top: `${ topPx }px`,
		boxSizing: 'border-box',
	} )
}

function initNavbarDropdowns() {
	const navbar = document.querySelector( '.tm-navbar' )
	if ( ! navbar ) {
		closeNavbarDropdowns = () => {}
		return
	}

	const parentItems = () => navbar.querySelectorAll( '.uk-navbar-nav > li.uk-parent' )

	const closeAll = () => {
		parentItems().forEach( ( li ) => {
			li.classList.remove( 'uk-open' )
			li.setAttribute( 'aria-expanded', 'false' )
			const dd = li.querySelector( ':scope > .uk-dropdown' )
			if ( dd ) {
				dd.setAttribute( 'aria-hidden', 'true' )
				clearDropdownPosition( dd )
			}
		} )
	}

	closeNavbarDropdowns = closeAll

	let rafId = null
	const repositionOpen = () => {
		if ( rafId !== null ) return
		rafId = requestAnimationFrame( () => {
			rafId = null
			navbar.querySelectorAll( '.uk-navbar-nav > li.uk-parent.uk-open' ).forEach( ( li ) => {
				applyBodyJustify( li )
			} )
		} )
	}

	navbar.addEventListener( 'click', ( e ) => {
		const link = e.target.closest( 'a' )
		if ( ! link || ! navbar.contains( link ) ) return
		if ( link.closest( '.uk-dropdown' ) ) return

		const li = link.closest( 'li.uk-parent' )
		if ( ! li || ! navbar.contains( li ) || link.parentElement !== li ) return

		const dropdown = li.querySelector( ':scope > .uk-dropdown' )
		if ( ! dropdown ) return

		e.preventDefault()

		const wasOpen = li.classList.contains( 'uk-open' )
		closeAll()

		if ( ! wasOpen ) {
			li.classList.add( 'uk-open' )
			li.setAttribute( 'aria-expanded', 'true' )
			dropdown.setAttribute( 'aria-hidden', 'false' )
			applyBodyJustify( li )
		}
	} )

	document.addEventListener( 'click', ( e ) => {
		if ( navbar.contains( e.target ) ) return
		closeAll()
	} )

	window.addEventListener( 'scroll', repositionOpen, { passive: true } )
	window.addEventListener( 'resize', repositionOpen )
}

/**
 * Shared behavior for toggleable overlays (off-canvas, search, etc.).
 *
 * @param {{
 *   root: HTMLElement,
 *   toggle: HTMLElement,
 *   activeClass?: string,
 *   bodyOpenClass?: string,
 *   lockBodyScroll?: boolean,
 *   closeButtonsSelector?: string,
 *   onOpen?: () => void,
 *   onClose?: () => void,
 * }} options
 * @return {{ root: HTMLElement, closeIfOpen: () => boolean }}
 */
function initToggleOverlay( options ) {
	const {
		root,
		toggle,
		activeClass = 'uk-active',
		bodyOpenClass = '',
		lockBodyScroll = false,
		closeButtonsSelector = '',
		onOpen = () => {},
		onClose = () => {},
	} = options

	const close = () => {
		root.classList.remove( activeClass )
		root.setAttribute( 'aria-hidden', 'true' )
		toggle.setAttribute( 'aria-expanded', 'false' )
		if ( bodyOpenClass ) {
			document.body.classList.remove( bodyOpenClass )
		}
		if ( lockBodyScroll ) {
			document.body.style.overflow = ''
		}
		onClose()
	}

	const isOpen = () => root.classList.contains( activeClass )

	const open = () => {
		root.classList.add( activeClass )
		root.setAttribute( 'aria-hidden', 'false' )
		toggle.setAttribute( 'aria-expanded', 'true' )
		if ( bodyOpenClass ) {
			document.body.classList.add( bodyOpenClass )
		}
		if ( lockBodyScroll ) {
			document.body.style.overflow = 'hidden'
		}
		onOpen()
	}

	const closeIfOpen = () => {
		if ( ! isOpen() ) return false
		close()
		return true
	}

	toggle.addEventListener( 'click', ( e ) => {
		e.preventDefault()
		isOpen() ? close() : open()
	} )

	root.addEventListener( 'click', ( e ) => {
		if ( e.target === root ) close()
	} )

	if ( closeButtonsSelector ) {
		root.querySelectorAll( closeButtonsSelector ).forEach( ( btn ) => {
			btn.addEventListener( 'click', close )
		} )
	}

	return { root, closeIfOpen }
}

/**
 * Replaces UIkit off-canvas (data-uk-offcanvas) using legacy .uk-offcanvas markup/CSS.
 *
 * @param {{ onOpen?: () => void, onClose?: () => void }} [hooks]
 * @return {{ closeIfOpen: () => boolean }}
 */
function initOffcanvas( hooks = {} ) {
	const { onOpen = () => {}, onClose = () => {} } = hooks
	const root = document.querySelector( '.js-offcanvas' )
	const toggle = document.querySelector( '.js-headerOffcanvas' )
	if ( ! root || ! toggle ) {
		return { closeIfOpen: () => false }
	}

	const { closeIfOpen } = initToggleOverlay( {
		root,
		toggle,
		bodyOpenClass: 'is-offcanvasOpen',
		lockBodyScroll: true,
		closeButtonsSelector: '.js-offcanvasClose',
		onOpen: () => {
			closeNavbarDropdowns()
			onOpen()
		},
		onClose,
	} )

	const nav = root.querySelector( '.uk-nav-offcanvas' )
	if ( nav ) {
		nav.addEventListener( 'click', ( e ) => {
			const link = e.target.closest( 'a' )
			if ( ! link || ! nav.contains( link ) ) return

			const li = link.parentElement
			if ( li?.classList.contains( 'uk-parent' ) ) {
				const sub = li.querySelector( ':scope > .uk-nav-sub' )
				if ( sub ) {
					e.preventDefault()
					li.classList.toggle( 'uk-open' )
					return
				}
			}
			closeIfOpen()
		} )
	}

	return { closeIfOpen }
}

/**
 * Replaces UIkit search toggle/drop (legacy href="#search") with explicit JS toggling.
 *
 * @param {{ onOpen?: () => void, onClose?: () => void }} [hooks]
 * @return {{ closeIfOpen: () => boolean }}
 */
function initSearch( hooks = {} ) {
	const { onOpen = () => {}, onClose = () => {} } = hooks
	const toggle = document.querySelector( '.js-headerSearchToggle' )
	if ( ! toggle ) {
		return { closeIfOpen: () => false }
	}

	const searchId = toggle.getAttribute( 'aria-controls' )
	const root = searchId ? document.getElementById( searchId ) : document.querySelector( '.js-search' )
	if ( ! root ) {
		return { closeIfOpen: () => false }
	}

	const searchField = root.querySelector( 'input[type="search"], .uk-search-field' )

	const { closeIfOpen } = initToggleOverlay( {
		root,
		toggle,
		bodyOpenClass: 'is-searchOpen',
		lockBodyScroll: true,
		closeButtonsSelector: '.js-searchClose',
		onOpen: () => {
			closeNavbarDropdowns()
			onOpen()
			if ( searchField ) {
				requestAnimationFrame( () => searchField.focus() )
			}
		},
		onClose,
	} )

	return { closeIfOpen }
}

function getCookie( name ) {
	const pattern = new RegExp( '(?:^|; )' + name.replace( /([.$?*|{}()[\]\\/+^])/g, '\\$1' ) + '=([^;]*)' )
	const match = document.cookie.match( pattern )
	return match ? decodeURIComponent( match[ 1 ] ) : null
}

function setCookie( name, value, days ) {
	let expires = ''
	if ( days > 0 ) {
		const date = new Date( Date.now() + days * 864e5 )
		expires = '; expires=' + date.toUTCString()
	}
	const secure = window.location.protocol === 'https:' ? '; Secure' : ''
	document.cookie = `${ name }=${ encodeURIComponent( value ) }${ expires }; path=/; SameSite=Lax${ secure }`
}

function initAgeGate() {
	const gate = document.querySelector( '.js-ageGate' )
	if ( ! gate ) return

	if ( getCookie( AGE_COOKIE_NAME ) === '1' ) {
		gate.remove()
		return
	}

	const cookieDays = parseInt( gate.dataset.cookieDays, 10 )
	const noRedirect = gate.dataset.noRedirect || ''

	gate.hidden = false
	document.body.classList.add( 'is-ageGateOpen' )

	const close = () => {
		document.body.classList.remove( 'is-ageGateOpen' )
		gate.remove()
	}

	gate.querySelector( '.js-ageGateYes' )?.addEventListener( 'click', () => {
		setCookie( AGE_COOKIE_NAME, '1', isNaN( cookieDays ) ? 30 : cookieDays )
		close()
	} )

	gate.querySelector( '.js-ageGateNo' )?.addEventListener( 'click', () => {
		if ( noRedirect ) {
			window.location.href = noRedirect
		}
	} )
}

function initHeader() {
	initAgeGate()
	initNavbarDropdowns()

	let closeSearchIfOpen = () => false
	const { closeIfOpen: closeOffcanvasIfOpen } = initOffcanvas( {
		onOpen: () => closeSearchIfOpen(),
	} )
	const { closeIfOpen: closeSearch } = initSearch( {
		onOpen: () => closeOffcanvasIfOpen(),
	} )
	closeSearchIfOpen = closeSearch

	document.addEventListener( 'keydown', ( e ) => {
		if ( e.key !== 'Escape' ) return
		if ( closeOffcanvasIfOpen() ) return
		if ( closeSearchIfOpen() ) return
		closeNavbarDropdowns()
	} )
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initHeader )
} else {
	initHeader()
}
